import { Link, Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, titleCase } from '../../lib/format'
import type { Inspection, MiningSite } from './types'
import type { Organization } from '../../types'
import { Alert, Card, CardBody, CardHeader, Skeleton, StatusBadge } from '../../components/ui'

interface InspectionDetailResponse {
  ok: boolean
  item: Inspection
}

export default function InspectionDetail() {
  const { inspectionId } = useParams<{ inspectionId: string }>()

  const inspQuery = useQuery({
    queryKey: ['gov', 'inspection', inspectionId],
    queryFn: () => api.get<InspectionDetailResponse>(`/gov/inspections/${inspectionId}`),
    enabled: Boolean(inspectionId),
  })
  const orgQuery = useQuery({
    queryKey: ['gov', 'org-for-inspection', inspQuery.data?.item.org_id],
    queryFn: () => api.get<{ organization: Organization }>(`/organizations/${inspQuery.data!.item.org_id}`),
    enabled: Boolean(inspQuery.data?.item.org_id),
  })
  const siteQuery = useQuery({
    queryKey: ['gov', 'site-for-inspection', inspQuery.data?.item.site_id],
    queryFn: () => api.get<{ site: MiningSite }>(`/sites/${inspQuery.data!.item.site_id}`),
    enabled: Boolean(inspQuery.data?.item.site_id),
  })

  if (!inspectionId) return <Navigate to="/inspections" replace />
  if (inspQuery.isError) {
    return (
      <Alert tone="danger">
        {(inspQuery.error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/inspections">Back to inspections</Link></div>
      </Alert>
    )
  }
  if (inspQuery.isLoading || !inspQuery.data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  const insp = inspQuery.data.item
  const org = orgQuery.data?.organization
  const site = siteQuery.data?.site

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{org?.name || site?.name || 'Inspection'}</h1>
          <p className="page-head__sub">{formatDate(insp.inspection_date ?? insp.date)}</p>
        </div>
        <StatusBadge status={insp.outcome} />
      </div>

      <div className="grid grid--2" style={{ marginBottom: 'var(--sp-6)' }}>
        <Card>
          <CardHeader title="Findings" />
          <CardBody>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{insp.findings || 'No findings recorded.'}</p>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Recommendations" />
          <CardBody>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{insp.recommendations || 'None.'}</p>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader title="Details" />
        <CardBody>
          <div className="stack">
            <Row label="Organisation" value={org?.name || insp.org_id || '—'} />
            <Row label="Site" value={site?.name || '—'} />
            <Row label="Location" value={[titleCase(insp.district_id), titleCase(insp.region_id)].filter(Boolean).join(', ') || '—'} />
            <Row label="Follow-up" value={insp.follow_up_date ? formatDate(insp.follow_up_date) : 'None scheduled'} />
          </div>
        </CardBody>
      </Card>
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 550 }}>{value}</span>
    </div>
  )
}
